/**
 * DailyLimitModal (Slice B) — the conversion event.
 * ==================================================
 * Rendered once (in AppShell) and driven by demoLimitStore.demoLimitModalOpen.
 * The daily-limit moment is the Peak-End of a demo session and the single
 * self-standing conversion lever, so the overlay reads as a reward + a launch,
 * never a paywall. Anatomy (see the plan's "Conversion-Design" section):
 *   1. Peak / ownership — "You've gone deep today" + today's-work summary.
 *   2. Reframe / loss   — the database keeps growing (loss-aversion frame).
 *   3. ONE primary CTA  — value-framed "Talk to our team" → the quote flow.
 *   4. Social proof.
 *   5. Secondary        — "Email me tomorrow — resets in Xh".
 *   6. Tertiary         — "Keep browsing the full catalog — free" (no dead-end).
 *
 * First limit-hit of the day opens this modal; later same-day hits show a slim
 * toast instead (handled in demoLimitStore.openLimitModalOrToast → Toast).
 */
import { createPortal } from 'preact/compat'
import { useEffect, useRef, useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { trackEvent } from '../../modules/dataService.js'
import { requestDemoReminder } from '../../services/demoLimitService.js'
import { useResetCountdown } from '../../hooks/useResetCountdown.js'
import {
  demoLimitModalOpen,
  viewedToday,
  viewsLimit,
  closeLimitModal,
  hoursUntilReset,
} from '../../state/demoLimitStore.js'

// Cap the number of cell chips we render in the summary so the modal stays tight.
const MAX_SUMMARY_CHIPS = 6

export default function DailyLimitModal() {
  const open = demoLimitModalOpen.value
  const [, setLocation] = useLocation()
  const dialogRef = useRef(null)
  const primaryRef = useRef(null)
  const previouslyFocused = useRef(null)
  // 'idle' | 'pending' | 'done' | 'error' — drives the inline reminder
  // confirmation ('done') and the visible failure notice ('error'); the user
  // can retry from 'idle' or 'error'.
  const [reminderState, setReminderState] = useState('idle')

  const cells = viewedToday.value || []
  const limit = viewsLimit.value
  const hrs = hoursUntilReset()
  const countdown = useResetCountdown()

  // Fire the conversion-funnel "reached" event once per open, focus the primary
  // CTA (focus-trap entry), and restore focus on close.
  useEffect(() => {
    if (!open) return
    previouslyFocused.current = document.activeElement
    setReminderState('idle') // fresh state each time the modal opens
    trackEvent('Demo Limit Reached', {
      viewed_count: cells.length,
      daily_limit: limit,
    })
    // Defer focus so the portal node is mounted.
    const id = requestAnimationFrame(() => {
      if (primaryRef.current) primaryRef.current.focus()
    })
    return () => {
      cancelAnimationFrame(id)
      const prev = previouslyFocused.current
      if (prev && typeof prev.focus === 'function') prev.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  function close() {
    closeLimitModal()
  }

  function onOverlayClick(e) {
    // Only close when the backdrop itself (not the dialog) is clicked.
    if (e.target === e.currentTarget) close()
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation()
      close()
      return
    }
    // Minimal focus-trap: keep Tab within the dialog.
    if (e.key === 'Tab') {
      const focusables = dialogRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusables || focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }

  function onPrimaryCta() {
    // Demo users have no price → "Talk to our team" routes to the /experts
    // booking picker (three application specialists, 30/60-min Microsoft Booking
    // links) — not the billing /checkout form, and no longer the plain contact
    // form. lead_source="demo_limit_modal" is carried for attribution; here we
    // route + track only.
    trackEvent('Demo Limit Upgrade Clicked', {
      lead_source: 'demo_limit_modal',
      viewed_count: cells.length,
    })
    // Carry the attribution to the /experts page, which reads this and tags the
    // booking-click event with it (same mechanism as the /contact & /checkout flows).
    try {
      sessionStorage.setItem('demo_lead_source', 'demo_limit_modal')
    } catch (e) {
      /* sessionStorage unavailable (private mode) — attribution best-effort */
    }
    close()
    // "Talk to an expert" booking picker (pick a specialist, book 30/60 min).
    setLocation('/experts')
  }

  function onGetPro() {
    // Secondary CTA (Fix-Paket 2, Punkt 8): the *real* self-serve purchase path.
    // /plans (PlanSelectionPage) is the regular PRO purchase entry point used
    // everywhere else in the app (RegisterPage, SubscriptionPage, the
    // ProtectedRoute redirect all land here); from there the user picks a plan
    // and continues to /checkout. lead_source is tagged for attribution, mirroring
    // the primary CTA / sibling upsell modals.
    trackEvent('Demo Limit Upgrade Clicked', {
      lead_source: 'demo_limit_modal_getpro',
      viewed_count: cells.length,
    })
    try {
      sessionStorage.setItem('demo_lead_source', 'demo_limit_modal_getpro')
    } catch (e) {
      /* sessionStorage unavailable (private mode) — attribution best-effort */
    }
    close()
    setLocation('/plans')
  }

  async function onEmailReminder() {
    // Retry is allowed from 'idle' and 'error'; block only while a request is
    // in flight or already confirmed.
    if (reminderState === 'pending' || reminderState === 'done') return
    trackEvent('Demo Limit Reminder Requested', { hours_until_reset: hrs })
    setReminderState('pending')
    // Slice F: opt into the "your deep-dives reset tomorrow" reminder email.
    // The backend derives recipient + send time and de-dupes (NurtureEmailLog
    // UNIQUE), so this is idempotent. Fail quietly — never block the user.
    const result = await requestDemoReminder()
    if (result && result.scheduled) {
      setReminderState('done') // show inline confirmation, keep the modal open
    } else {
      // Backend declined ({scheduled:false}) or the call failed (null). Show a
      // visible error notice instead of silently snapping back to 'idle' — a
      // silent reset read to users as "the link does nothing" (Fix-Paket 2,
      // Punkt 9). The user can retry straight from this 'error' state.
      setReminderState('error')
    }
  }

  const summaryChips = cells.slice(0, MAX_SUMMARY_CHIPS)
  const extraCount = cells.length - summaryChips.length

  return createPortal(
    <div
      className="daily-limit-overlay"
      onClick={onOverlayClick}
      onKeyDown={onKeyDown}
    >
      <div
        className="daily-limit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="daily-limit-title"
        ref={dialogRef}
      >
        <button
          className="daily-limit-modal__close"
          onClick={close}
          aria-label="Close"
          type="button"
        >
          ×
        </button>

        <div className="daily-limit-modal__body">
          {/* 1. Peak / ownership */}
          <p className="daily-limit-modal__eyebrow">Today's deep work</p>
          <h2 className="daily-limit-modal__title" id="daily-limit-title">
            You have gone deep today
          </h2>

          {summaryChips.length > 0 && (
            <div className="daily-limit-modal__summary">
              <p className="daily-limit-modal__summary-lead">
                Cells you explored in detail today:
              </p>
              <div className="daily-limit-modal__cells">
                {summaryChips.map((c) => (
                  <span className="daily-limit-modal__cell-chip" key={c.product_id || c.name}>
                    {c.manufacturer ? `${c.manufacturer} ` : ''}
                    {c.name || c.product_id}
                  </span>
                ))}
                {extraCount > 0 && (
                  <span className="daily-limit-modal__cell-chip">+{extraCount} more</span>
                )}
              </div>
            </div>
          )}

          {/*
            2. Reframe / loss aversion.
            SLICE-E HOOK: Slice E injects the live "N cells added since you
            joined" number here (real created_at count via
            cellRecency.countAddedSince()). Rendered without a hard dependency
            on that number for now — prepend "The database has grown by <N>
            cells since you joined. " once that util lands.
          */}
          <p className="daily-limit-modal__reframe">
            If you want to continue digging (it's fun, we know), come back
            tomorrow. If you're lucky, new cells will already be waiting for
            you. If you don't want to wait: <strong>sign up for PRO</strong> —
            or talk to our team about which other options fit your needs.
          </p>

          {/* 3. One value-framed primary CTA (no price shown) */}
          <button
            className="daily-limit-modal__cta"
            onClick={onPrimaryCta}
            ref={primaryRef}
            type="button"
          >
            Talk to our team
          </button>
          <p className="daily-limit-modal__risk-off">
            No commitment — just a conversation. Most teams hear back within one
            business day.
          </p>

          {/* Secondary — self-serve purchase path (real PRO checkout via /plans) */}
          <button
            className="daily-limit-modal__cta-secondary"
            onClick={onGetPro}
            type="button"
          >
            Get PRO
          </button>

          {/* 4. Social proof */}
          <div className="daily-limit-modal__proof">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2l2.4 7.4H22l-6 4.4 2.3 7.2L12 16.6 5.7 21l2.3-7.2-6-4.4h7.6z" />
            </svg>
            Trusted by OEMs and tier-1 suppliers for measured cell data.
          </div>

          {/* 5. Secondary — reminder opt-in (micro-commitment) */}
          <div className="daily-limit-modal__secondary">
            <div className="daily-limit-modal__reminder">
              <span>Resets in {countdown.formatted}.</span>
              {reminderState === 'done' ? (
                <span className="daily-limit-modal__reminder-done" role="status">
                  ✓ Done — we'll email you when your deep-dives reset tomorrow.
                </span>
              ) : (
                <>
                  <button
                    className="daily-limit-modal__link-btn"
                    onClick={onEmailReminder}
                    disabled={reminderState === 'pending'}
                    type="button"
                  >
                    {reminderState === 'pending'
                      ? 'Setting a reminder…'
                      : reminderState === 'error'
                        ? 'Try again'
                        : 'Email me a reminder'}
                  </button>
                  {reminderState === 'error' && (
                    <span className="daily-limit-modal__reminder-error" role="alert">
                      Couldn't set a reminder — please try again.
                    </span>
                  )}
                </>
              )}
            </div>

            {/* 6. Tertiary — no dead-end */}
            <button
              className="daily-limit-modal__tertiary"
              onClick={close}
              type="button"
            >
              Keep browsing the full catalog — free
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
